import { useEffect, useMemo, useCallback } from 'react'

import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useGetCampaignTiktokDetailsQuery } from 'src/Redux/campaignTiktokDetailsApi'
import { useAppSelector, useAppDispatch } from 'src/Hooks/redux'
import { getUserEmail } from 'src/Redux/auth-process/userSlice/selectors'
import {
  getClosedTikTokTypeformIds,
  setClosedTikTokTypeformList,
} from 'src/Redux/typeform-process'
import { TiktokCampaignStatus } from 'src/Types/campaigns'

declare global {
  interface Window {
    tf?: {
      createPopup: (
        formId: string,
        options?: {
          hidden?: Record<string, string>
          onSubmit?: () => void
          onClose?: () => void
        },
      ) => { open: () => void }
    }
  }
}

const TYPEFORM_ID = 'Dddzi36c'

export const useTypeformForEndedTikTokCampaigns = (): void => {
  const { campaignID } = useParams<{ campaignID: string }>()
  const userEmail = useAppSelector(getUserEmail)
  const dispatch = useAppDispatch()
  const closedTikTokTypeformIds = useSelector(getClosedTikTokTypeformIds)

  const { data: campaignDetails } = useGetCampaignTiktokDetailsQuery(
    campaignID,
    {
      skip: !campaignID,
    },
  )

  const isAppropriateCampaignStatus = useMemo(() => {
    const status = campaignDetails?.status
    if (!status) {
      return false
    }

    return [
      TiktokCampaignStatus.ENDED,
      TiktokCampaignStatus.CANCELED,
      TiktokCampaignStatus.FINISHED,
      TiktokCampaignStatus.STOPPED,
    ].includes(status)
  }, [campaignDetails?.status])

  const isClosedCampaign = Boolean(
    closedTikTokTypeformIds?.includes(campaignDetails?.id?.toString() ?? ''),
  )

  const markClosed = useCallback(() => {
    const id = String(campaignDetails?.id ?? '')
    if (!id) {
      return
    }
    const next = Array.from(new Set([...closedTikTokTypeformIds, id]))
    dispatch(setClosedTikTokTypeformList(next))
  }, [campaignDetails?.id, closedTikTokTypeformIds, dispatch])

  useEffect(() => {
    const hasRequiredCampaignData = Boolean(
      campaignDetails?.id &&
      campaignDetails?.type &&
      campaignDetails?.track?.title,
    )

    const hasUserEmail = Boolean(userEmail)
    const shouldRun =
      isAppropriateCampaignStatus &&
      !isClosedCampaign &&
      hasRequiredCampaignData &&
      hasUserEmail

    if (shouldRun) {
      const scriptId = 'typeform-embed-script'
      let script = document.getElementById(scriptId) as HTMLScriptElement | null
      let scriptAppended = false
      if (!script) {
        script = document.createElement('script')
        script.id = scriptId
        script.src = 'https://embed.typeform.com/next/embed.js'
        script.async = true
        document.body.appendChild(script)
        scriptAppended = true
      }

      const openPopup = (): void => {
        if (!window.tf || !window.tf.createPopup) {
          return
        }

        if (!userEmail) {
          return
        }

        if (
          !campaignDetails?.id ||
          !campaignDetails?.type ||
          !campaignDetails?.track?.title
        ) {
          return
        }

        const popup = window.tf.createPopup(TYPEFORM_ID, {
          hidden: {
            email: userEmail,
            campaign_id: campaignDetails.id.toString(),
            campaign_type: campaignDetails.type,
            campaign_name: campaignDetails.track.title,
            is_feed: 'no',
            pricing_model: campaignDetails.pricingModel,
          },
          onSubmit: markClosed,
          onClose: markClosed,
        })
        popup.open()
      }

      if (script) {
        script.onload = openPopup
      }

      if (window.tf) {
        openPopup()
      }

      return () => {
        if (scriptAppended && script && document.body.contains(script)) {
          document.body.removeChild(script)
        }
      }
    }
    return void 0
  }, [
    campaignDetails?.id,
    campaignDetails?.track?.title,
    campaignDetails?.type,
    campaignDetails?.pricingModel,
    closedTikTokTypeformIds,
    dispatch,
    isAppropriateCampaignStatus,
    isClosedCampaign,
    markClosed,
    userEmail,
  ])
}
